/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019-2024 (original work) Open Assessment Technologies SA
 *
 */

import router from '@/core/router';
import { __ } from '@oat-sa-private/ui-core';
import { user } from '@/stores/auth.store';

let container;

/**
 * The provided controller object will be extended with page controller functions.
 * Usage:
 *  import pageController from 'controller/page';
 *  const controller = pageController(controller, {
 *      foo() {
 *          // in controller functions, page controller functions are available
 *          this.router();
 *      }
 *  });
 *
 * @param {Object} controller - controller object that should be extended
 * @returns {Object} controller object that is extended with page controller functions.
 */
export default controller =>
    Object.assign(
        {
            /**
             * Flag defining if component has been destroyed
             */
            destroyed: false,
            /**
             * Getter of page container
             * @returns {Element} Page container element
             */
            get container() {
                return container || (container = document.querySelector('#page'));
            },
            /**
             * Getter of router
             * @returns {Router} Router instance
             */
            get router() {
                return router;
            },
            setPageTitle(title) {
                document.title = __('%s | TAO', title);
                const pageTitle = document.getElementById('page-title');
                pageTitle.innerText = title;
            },
            /**
             * Common prepare function before controller starts
             */
            prepare() {
                this.container.classList.add(this.name);
            },
            /**
             * Common clean function after controller stopped
             */
            clean() {
                const pageContainer = this.container;
                pageContainer.classList.remove(this.name);
            },
            /**
             * Logout event handler
             * @returns {Promise<void>}
             */
            async logout() {
                try {
                    await user.logout();
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(err.message);
                }
            },
            /**
             * Shows a feedback for a limited amount of time
             * @deprecated Use notifications API instead https://github.com/oat-sa/live-design-system/tree/master/packages/components/notificationContainer
             * @param {string} status
             * @param {string} message
             */
            showFeedback(status, message) {
                this.component.$set({ feedback: { status, message } });
            },
            /**
             * @param {Object} Component - Svelte component constructor
             * @param {any} [props] - component props
             */
            mount(Component, props) {
                this.component = new Component({
                    target: this.container,
                    props
                });
            },
            /**
             * Updates page component with props
             * @param {Object} props - new props to pass and update the component
             */
            update(props) {
                if (this.component && !this.destroyed) {
                    this.component.$set(props);
                }
            },
            /**
             * Function to be called before destroy method
             * Can be asynchronous
             */
            async beforeDestroy() {},
            /**
             * Destroy the component
             */
            async destroy() {
                await this.beforeDestroy();
                this.destroyed = true;
                if (this.component) {
                    this.component.$destroy();
                }
            }
        },
        controller
    );
